import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useReadyForBillingPettyWorkCount } from "features/PettyWorks/hooks/useReadyForBillingPettyWorkCount";
import { setReadyForBillingPettyWorkCountRefetch } from "features/PettyWorks/pettyWorkReadyForBillingCountInvalidate";

export type ReadyForBillingPettyWorkCountContextValue = {
  count: number;
  isLoading: boolean;
  refetch: () => void;
};

const ReadyForBillingPettyWorkCountContext =
  createContext<ReadyForBillingPettyWorkCountContextValue | null>(null);

const fallback: ReadyForBillingPettyWorkCountContextValue = {
  count: 0,
  isLoading: false,
  refetch: () => {},
};

export function ReadyForBillingPettyWorkCountProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { can } = useAccess();
  const enabled = can(Permissions.PETTY_WORK.SUBMIT_BILLING);
  const { count, isLoading, refetch } = useReadyForBillingPettyWorkCount(enabled);

  useEffect(() => {
    if (!enabled) {
      setReadyForBillingPettyWorkCountRefetch(null);
      return;
    }
    setReadyForBillingPettyWorkCountRefetch(refetch);
    return () => {
      setReadyForBillingPettyWorkCountRefetch(null);
    };
  }, [enabled, refetch]);

  const value = useMemo(
    () => ({ count, isLoading, refetch }),
    [count, isLoading, refetch],
  );

  return (
    <ReadyForBillingPettyWorkCountContext.Provider value={value}>
      {children}
    </ReadyForBillingPettyWorkCountContext.Provider>
  );
}

export function useReadyForBillingPettyWorkCountOptional(): ReadyForBillingPettyWorkCountContextValue {
  const ctx = useContext(ReadyForBillingPettyWorkCountContext);
  return ctx ?? fallback;
}
